// Object tree pane — flat indented hierarchical view of the current MapJSON.
// Rows are clickable; Cmd/Ctrl-click toggles multi-select; Shift-click adds.
// Section headers have a [+] button that inserts a sensible default and
// selects it.

import type { EditorState } from './EditorState';
import type { MapJSON, ObjectNode, LightDef, BehaviorDef, BoxObject } from '../../maps/MapJSON';
import { renderIcon, type IconNode } from './icons';
import {
  Map as MapIcon, Cloud, Lightbulb, Box, Cog, Music, Plus, ChevronRight, ChevronDown,
  Layers, Circle, Cylinder, Cuboid, Square, Boxes, Eye, EyeOff, Image as ImageIcon
} from 'lucide';

export class ObjectTreePane {
  readonly root: HTMLElement;
  private state: EditorState;
  private unsubDoc: () => void;
  private unsubSel: () => void;

  constructor(parent: HTMLElement, state: EditorState) {
    this.state = state;
    this.root = document.createElement('div');
    this.root.className = 'editor-tree';
    parent.appendChild(this.root);
    this.unsubDoc = this.state.subscribeDoc(() => this.render());
    this.unsubSel = this.state.subscribeSelection(() => this.render());
    this.render();
  }

  dispose(): void {
    this.unsubDoc();
    this.unsubSel();
    this.root.remove();
  }

  private render(): void {
    const { json, selectionIds, hiddenIds } = this.state.get();
    const sel = new Set(selectionIds);
    const rows: string[] = [];

    rows.push(rowHtml('Map', 'map', sel.has('map'), 0, MapIcon));
    rows.push(rowHtml(`Sky · ${json.sky?.preset ?? 'none'}`, 'sky', sel.has('sky'), 1, Cloud));
    rows.push(rowHtml(`IBL · ${json.ibl?.mode ?? 'roomDefault'}`, 'ibl', sel.has('ibl'), 1, ImageIcon));
    rows.push(sectionHtml('Lights', 1, Lightbulb, 'add-light'));
    for (const l of json.lights ?? []) {
      const id = l.id ?? `(${l.type})`;
      const selId = `light:${id}`;
      rows.push(rowHtml(`${l.type} · ${id}`, selId, sel.has(selId), 2, Lightbulb));
    }
    rows.push(sectionHtml('Objects', 1, Boxes, 'add-object'));
    for (const n of json.objects ?? []) {
      walkObject(rows, n, 2, sel, hiddenIds);
    }
    rows.push(sectionHtml('Behaviors', 1, Cog, 'add-behavior'));
    for (const b of json.behaviors ?? []) {
      const selId = `behavior:${b.id}`;
      rows.push(rowHtml(`${b.id} · ${b.module}`, selId, sel.has(selId), 2, Cog));
    }
    rows.push(rowHtml('Audio', 'audio', sel.has('audio'), 1, Music));
    rows.push(rowHtml('Picker Card', 'picker', sel.has('picker'), 1, ImageIcon));

    this.root.innerHTML = rows.join('');
    this.root.onclick = (e) => {
      const target = e.target as HTMLElement;
      const eye = target.closest('[data-eye]') as HTMLElement | null;
      if (eye) {
        e.stopPropagation();
        const id = eye.dataset.eye!;
        const isHidden = this.state.get().hiddenIds.has(id);
        this.state.setHidden(id, !isHidden);
        return;
      }
      const addBtn = target.closest('[data-add]') as HTMLElement | null;
      if (addBtn) {
        e.stopPropagation();
        this.handleAdd(addBtn.dataset.add!);
        return;
      }
      const el = target.closest('[data-sel]') as HTMLElement | null;
      if (!el) return;
      const id = el.dataset.sel!;
      if (id.startsWith('section:')) return;
      const additive = e.shiftKey || e.ctrlKey || e.metaKey;
      if (additive) {
        this.state.toggleSelection(id);
      } else {
        this.state.setSelection([id], id);
      }
    };
  }

  private handleAdd(kind: string): void {
    const { json } = this.state.get();
    if (kind === 'add-light') {
      const id = `light-${(json.lights?.length ?? 0) + 1}`;
      const light: LightDef = {
        type: 'point', id,
        position: [0, 4, 0], color: '#ffffff', intensity: 1.0, range: 20, decay: 2
      };
      this.state.commit({ ...json, lights: [...(json.lights ?? []), light] }, { kind: 'structural' });
      this.state.setSelection([`light:${id}`]);
    } else if (kind === 'add-object') {
      const id = nextUniqueId(json, 'box');
      const box: BoxObject = {
        id, type: 'box',
        size: [2, 2, 2],
        transform: { p: [0, 1, 0] },
        material: { color: '#cccccc', roughness: 0.8, metalness: 0.0 },
        collider: { kind: 'auto', body: 'static', friction: 0.9, restitution: 0.05 }
      };
      this.state.commit({ ...json, objects: [...(json.objects ?? []), box] }, { kind: 'structural' });
      this.state.setSelection([`obj:${id}`]);
    } else if (kind === 'add-behavior') {
      const id = `behavior-${(json.behaviors?.length ?? 0) + 1}`;
      const b: BehaviorDef = { id, module: '', params: {} };
      this.state.commit({ ...json, behaviors: [...(json.behaviors ?? []), b] }, { kind: 'structural' });
      this.state.setSelection([`behavior:${id}`]);
    }
  }
}

function walkObject(
  rows: string[], node: ObjectNode, depth: number,
  sel: Set<string>, hidden: ReadonlySet<string>
): void {
  const id = node.id ?? `(${node.type})`;
  const selId = `obj:${id}`;
  const icon = iconForType(node.type);
  rows.push(rowHtml(`${node.type} · ${id}`, selId, sel.has(selId), depth, icon, hidden.has(id)));
  if (node.type === 'group' && node.children?.length) {
    for (const c of node.children) walkObject(rows, c, depth + 1, sel, hidden);
  }
}

function rowHtml(
  label: string, selId: string, focused: boolean, depth: number, icon: IconNode,
  isHidden = false
): string {
  const cls = ['editor-tree-row'];
  if (focused) cls.push('focused');
  if (isHidden) cls.push('is-hidden');
  const pad = depth * 14 + 8;
  const eyeIcon = isHidden ? EyeOff : Eye;
  const eyeBtn = selId.startsWith('obj:') || selId.startsWith('light:')
    ? `<button class="editor-tree-eye" data-eye="${escapeAttr(selId.slice(selId.indexOf(':') + 1))}" title="Toggle visibility">${renderIcon(eyeIcon, 14)}</button>`
    : '';
  return `<div class="${cls.join(' ')}" data-sel="${escapeAttr(selId)}" style="padding-left: ${pad}px">
    <span class="editor-tree-icon ed-icon">${renderIcon(icon, 13)}</span>
    <span class="editor-tree-label">${escapeHtml(label)}</span>
    ${eyeBtn}
  </div>`;
}

function sectionHtml(
  label: string, depth: number, icon: IconNode, addKind: string
): string {
  const pad = depth * 14 + 8;
  return `<div class="editor-tree-row editor-tree-section" data-sel="section:${addKind}" style="padding-left: ${pad}px">
    <span class="editor-tree-icon ed-icon">${renderIcon(icon, 13)}</span>
    <span class="editor-tree-label">${escapeHtml(label)}</span>
    <button class="ed-iconbtn compact" data-add="${addKind}" title="Add ${label.toLowerCase()}">${renderIcon(Plus, 13)}</button>
  </div>`;
}

function iconForType(t: string): IconNode {
  switch (t) {
    case 'box': return Box;
    case 'sphere': return Circle;
    case 'cylinder': return Cylinder;
    case 'capsule': return Cuboid;
    case 'plane': return Square;
    case 'circle': return Circle;
    case 'group': return Layers;
    case 'modelRef': return Cuboid;
    default: return ChevronRight;
  }
}

function nextUniqueId(json: MapJSON, prefix: string): string {
  const existing = new Set<string>();
  const visit = (nodes: ObjectNode[] | undefined) => {
    if (!nodes) return;
    for (const n of nodes) {
      if (n.id) existing.add(n.id);
      if (n.type === 'group') visit(n.children);
    }
  };
  visit(json.objects);
  let i = 1;
  while (existing.has(`${prefix}-${i}`)) i++;
  return `${prefix}-${i}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' : '&#39;'
  ));
}
function escapeAttr(s: string): string { return escapeHtml(s); }

// keep the imports live for type narrowing in future edits
void (null as unknown as MapJSON);
void ChevronDown;
